import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useIdentity } from "../../auth/useIdentity";
import { getContactDetails, getOwnAvatar } from "../../api/account";

/**
 * The signed-in person's photo as an object URL, or null. Fetched once per
 * picture: the contact details carry its hash, so a new photo is a new query.
 */
export function useOwnAvatar(): string | null {
  const identity = useIdentity();
  const ns = identity.rootNamespace ?? identity.namespace ?? "";
  const contact = useQuery({
    queryKey: ["contactDetails", ns],
    enabled: Boolean(identity.token && ns),
    retry: false,
    staleTime: 60_000,
    queryFn: ({ signal }) => getContactDetails(identity, ns, signal),
  });
  const sha = contact.data?.avatar ?? "";
  const pic = useQuery({
    queryKey: ["ownAvatar", ns, sha],
    enabled: Boolean(identity.token && ns && sha),
    retry: false,
    staleTime: Infinity,
    queryFn: ({ signal }) => getOwnAvatar(identity, ns, signal),
  });
  const blob = sha ? pic.data ?? null : null;
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return;
    }
    const u = URL.createObjectURL(blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);
  return url;
}
